import { jsPDF } from "jspdf";
import {
  examDocumentFallbackTitle,
  maximumScoreLabel,
} from "../../data/copy/usClassroomTerms";
import { pdfLayout } from "./theme";
import {
  getQuestionPaperContentWidth,
  questionPaperPdfLayout,
  questionPaperPdfTheme,
} from "./questionPaperTheme";
import { normalizePdfText } from "./normalizePdfText";

const theme = questionPaperPdfTheme;
const layout = questionPaperPdfLayout;

function coerceText(value) {
  return normalizePdfText(value);
}

function hexToRgb(hex) {
  const normalized = hex.replace("#", "");
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function setFill(pdf, hex) {
  pdf.setFillColor(...hexToRgb(hex));
}

function setDraw(pdf, hex) {
  pdf.setDrawColor(...hexToRgb(hex));
}

function setText(pdf, hex) {
  pdf.setTextColor(...hexToRgb(hex));
}

function setFont(pdf, style, size) {
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
}

function writeWrapped(pdf, text, x, y, maxWidth, options = {}) {
  const { fontStyle = "normal", fontSize = 10, color = theme.text, lineHeight = layout.lineHeight } =
    options;
  if (!text) {
    return y;
  }
  setFont(pdf, fontStyle, fontSize);
  setText(pdf, color);
  const lines = pdf.splitTextToSize(coerceText(text), maxWidth);
  let cursorY = y;
  for (const line of lines) {
    pdf.text(line, x, cursorY);
    cursorY += lineHeight;
  }
  return cursorY;
}

function createQuestionPaperContext(pdf, document) {
  const marginX = layout.marginX;
  const contentWidth = getQuestionPaperContentWidth(pdfLayout.pageWidth);

  return {
    pdf,
    document,
    marginX,
    contentWidth,
    contentTop: layout.contentTop,
    contentBottom: layout.contentBottom,
    y: layout.contentTop,
    headerRendered: false,
  };
}

function addPage(ctx) {
  ctx.pdf.addPage();
  ctx.y = ctx.contentTop;
  setFill(ctx.pdf, theme.pageBg);
  ctx.pdf.rect(0, 0, pdfLayout.pageWidth, pdfLayout.pageHeight, "F");
}

function ensureSpace(ctx, blockHeight) {
  if (ctx.y + blockHeight > ctx.contentBottom) {
    addPage(ctx);
    return true;
  }
  return false;
}

function estimateSectionHeaderHeight(ctx, slide) {
  let height = 10;
  if (slide.subtitle) {
    setFont(ctx.pdf, "normal", 8);
    const lines = ctx.pdf.splitTextToSize(
      coerceText(slide.subtitle),
      ctx.contentWidth - 30,
    );
    height += 3 + Math.max(0, lines.length - 1) * 4;
  }
  return Math.max(layout.sectionHeaderHeight, height);
}

function estimateWrittenQuestionHeight(ctx, slide) {
  const textWidth = ctx.contentWidth - layout.questionNumberSize - 30;
  setFont(ctx.pdf, "normal", slide.isLongAnswer ? 10.5 : 10);
  const promptLines = ctx.pdf.splitTextToSize(coerceText(slide.text), textWidth);
  const hasAnswerLines = (slide.lineCount ?? 0) > 0;
  const lineGap = slide.isLongAnswer ? 7 : 5;
  const lineBlockHeight = hasAnswerLines ? slide.lineCount * lineGap + 10 : 0;
  const promptHeight = promptLines.length * (layout.lineHeight + (slide.isLongAnswer ? 1 : 0.5));
  const answerHeight = slide.correctAnswer ? 14 : 0;
  return 12 + promptHeight + lineBlockHeight + answerHeight + layout.cardPadding;
}

function estimateMcqQuestionHeight(ctx, slide) {
  const textWidth = ctx.contentWidth - layout.questionNumberSize - 8;
  setFont(ctx.pdf, "normal", 10);
  const promptLines = ctx.pdf.splitTextToSize(coerceText(slide.text), textWidth);
  const optionRows = Math.ceil((slide.options?.length ?? 0) / 2);
  const answerHeight = slide.correctAnswer ? 10 : 0;
  return (
    12 +
    promptLines.length * (layout.lineHeight + 0.5) +
    optionRows * 14 +
    answerHeight +
    layout.cardPadding
  );
}

function estimateSlideBlockHeight(ctx, slide) {
  switch (slide.kind) {
    case "exam-section-header":
      return estimateSectionHeaderHeight(ctx, slide) + 3;
    case "exam-student-mcq-answer":
      return layout.studentMcqAnswerRowHeight + layout.questionGap;
    case "exam-mcq-question":
      return estimateMcqQuestionHeight(ctx, slide) + layout.questionGap;
    case "exam-written-question":
      return estimateWrittenQuestionHeight(ctx, slide) + layout.questionGap;
    case "exam-instructions": {
      const instructions = slide.instructions ?? [];
      setFont(ctx.pdf, "normal", 9.5);
      let contentHeight = 13;
      for (const instruction of instructions) {
        const lines = ctx.pdf.splitTextToSize(
          `- ${coerceText(instruction)}`,
          ctx.contentWidth - 12,
        );
        contentHeight += lines.length * layout.lineHeight + 1;
      }
      return contentHeight + 4 + layout.sectionGap;
    }
    case "exam-paper-header":
      return measureExamPaperHeaderHeight(ctx, slide) + layout.sectionGap;
    case "exam-syllabus-summary": {
      const rows = Math.ceil((slide.items?.length ?? 0) / 3);
      return 16 + rows * 22 + layout.sectionGap;
    }
    default:
      return 24;
  }
}

function ensureSectionHeaderSpace(ctx, slide, nextSlide) {
  const headerHeight = estimateSectionHeaderHeight(ctx, slide);
  const followingHeight = nextSlide ? estimateSlideBlockHeight(ctx, nextSlide) : 22;
  ensureSpace(ctx, headerHeight + followingHeight + layout.sectionGap);
}

function drawRoundedRect(pdf, x, y, width, height, style = "S") {
  pdf.roundedRect(x, y, width, height, 2, 2, style);
}

function drawMarksBadge(ctx, label, x, y) {
  const badgeWidth = Math.max(18, ctx.pdf.getTextWidth(label) + 6);
  setFill(ctx.pdf, theme.primary);
  drawRoundedRect(ctx.pdf, x, y - 4, badgeWidth, 7, "F");
  setFont(ctx.pdf, "bold", 7);
  setText(ctx.pdf, theme.white);
  ctx.pdf.text(label.toUpperCase(), x + 3, y);
  return badgeWidth;
}

function drawQuestionNumberBadge(ctx, number, x, y) {
  const size = layout.questionNumberSize;
  setFill(ctx.pdf, theme.questionBadge);
  drawRoundedRect(ctx.pdf, x, y - size + 2, size, size, "F");
  setFont(ctx.pdf, "bold", 10);
  setText(ctx.pdf, theme.white);
  ctx.pdf.text(String(number), x + size / 2, y - 1, { align: "center" });
  return size;
}

function measureExamPaperHeaderHeight(ctx, slide) {
  const textWidth = ctx.contentWidth - 10;
  let height = 10;

  setFont(ctx.pdf, "bold", 14);
  height += ctx.pdf.splitTextToSize(coerceText(slide.title), textWidth).length * 6;

  if (slide.subjectLine) {
    height += 1;
    setFont(ctx.pdf, "normal", 10);
    height += ctx.pdf.splitTextToSize(coerceText(slide.subjectLine), textWidth).length * 5;
  }

  height += 8;
  const colWidth = (ctx.contentWidth - 10) / 3;
  const metaValues = [slide.duration, slide.maximumMarks, slide.examFormat];
  setFont(ctx.pdf, "bold", 9.5);
  let maxValueLines = 1;
  for (const value of metaValues) {
    const lines = ctx.pdf.splitTextToSize(coerceText(value), colWidth - 2);
    maxValueLines = Math.max(maxValueLines, lines.length);
  }

  height += 5 + maxValueLines * 4.5 + 6;
  return Math.max(layout.headerHeight, height);
}

function renderExamPaperHeader(ctx, slide) {
  const headerHeight = measureExamPaperHeaderHeight(ctx, slide);
  ensureSpace(ctx, headerHeight);
  const startY = ctx.y;

  setFill(ctx.pdf, theme.primary);
  drawRoundedRect(ctx.pdf, ctx.marginX, startY, ctx.contentWidth, headerHeight, "F");

  let textY = startY + 10;
  textY = writeWrapped(ctx.pdf, slide.title, ctx.marginX + 5, textY, ctx.contentWidth - 10, {
    fontStyle: "bold",
    fontSize: 14,
    color: theme.white,
    lineHeight: 6,
  });

  if (slide.subjectLine) {
    textY = writeWrapped(ctx.pdf, slide.subjectLine, ctx.marginX + 5, textY + 1, ctx.contentWidth - 10, {
      fontSize: 10,
      color: theme.white,
      lineHeight: 5,
    });
  }

  textY += 5;
  ctx.pdf.setDrawColor(255, 255, 255);
  ctx.pdf.setLineWidth(0.2);
  ctx.pdf.line(ctx.marginX + 5, textY, ctx.marginX + ctx.contentWidth - 5, textY);

  const colWidth = (ctx.contentWidth - 10) / 3;
  const metaLabelY = textY + 5;
  const metaValueY = metaLabelY + 4.5;
  const metaItems = [
    { label: "Time Duration", value: slide.duration },
    { label: maximumScoreLabel(), value: slide.maximumMarks },
    { label: "Exam Format", value: slide.examFormat },
  ];

  metaItems.forEach((item, index) => {
    const x = ctx.marginX + 5 + colWidth * index;
    setFont(ctx.pdf, "bold", 6.5);
    setText(ctx.pdf, theme.white);
    ctx.pdf.text(item.label.toUpperCase(), x, metaLabelY);
    writeWrapped(ctx.pdf, item.value, x, metaValueY, colWidth - 2, {
      fontStyle: "bold",
      fontSize: 9.5,
      color: theme.white,
      lineHeight: 4.5,
    });
  });

  ctx.y = startY + headerHeight + layout.sectionGap;
  ctx.headerRendered = true;
}

function renderExamInstructions(ctx, slide) {
  const instructions = slide.instructions ?? [];
  setFont(ctx.pdf, "normal", 9.5);

  let contentHeight = 13;
  for (const instruction of instructions) {
    const lines = ctx.pdf.splitTextToSize(`- ${coerceText(instruction)}`, ctx.contentWidth - 12);
    contentHeight += lines.length * layout.lineHeight + 1;
  }
  const blockHeight = contentHeight + 4;
  ensureSpace(ctx, blockHeight);
  setFill(ctx.pdf, theme.primarySoft);
  setDraw(ctx.pdf, theme.border);
  ctx.pdf.setLineWidth(0.3);
  drawRoundedRect(ctx.pdf, ctx.marginX, ctx.y, ctx.contentWidth, blockHeight, "FD");

  let textY = ctx.y + 7;
  setFont(ctx.pdf, "bold", 8);
  setText(ctx.pdf, theme.primaryDark);
  ctx.pdf.text("GENERAL INSTRUCTIONS", ctx.marginX + 5, textY);

  textY += 6;
  setFont(ctx.pdf, "normal", 9.5);
  setText(ctx.pdf, theme.instructionsText);
  for (const instruction of instructions) {
    const lines = ctx.pdf.splitTextToSize(`- ${coerceText(instruction)}`, ctx.contentWidth - 12);
    for (const line of lines) {
      ctx.pdf.text(line, ctx.marginX + 7, textY);
      textY += layout.lineHeight;
    }
    textY += 1;
  }

  ctx.y += blockHeight + layout.sectionGap;
}

function renderExamSectionHeader(ctx, slide) {
  const blockHeight = estimateSectionHeaderHeight(ctx, slide);

  setFill(ctx.pdf, theme.sectionBg);
  setDraw(ctx.pdf, theme.border);
  ctx.pdf.setLineWidth(0.3);
  drawRoundedRect(ctx.pdf, ctx.marginX, ctx.y, ctx.contentWidth, blockHeight, "FD");

  const titleY = ctx.y + 6;
  setFont(ctx.pdf, "bold", 10);
  setText(ctx.pdf, theme.primaryDark);
  ctx.pdf.text(slide.title, ctx.marginX + 4, titleY);

  if (slide.subtitle) {
    setFont(ctx.pdf, "normal", 8);
    setText(ctx.pdf, theme.primaryMuted);
    ctx.pdf.text(slide.subtitle, ctx.marginX + 4, titleY + 4.5);
  }

  drawMarksBadge(ctx, slide.marksLabel, ctx.marginX + ctx.contentWidth - 24, titleY);

  ctx.y += blockHeight + 3;
}

function renderMcqOption(ctx, option, x, y, width) {
  const rowHeight = 12;
  setDraw(ctx.pdf, theme.cardBorder);
  ctx.pdf.setLineWidth(0.2);
  setFill(ctx.pdf, theme.white);
  drawRoundedRect(ctx.pdf, x, y, width, rowHeight, "FD");

  const circleX = x + 4;
  const circleY = y + 4;
  setDraw(ctx.pdf, "#CBD5E1");
  ctx.pdf.setLineWidth(0.4);
  ctx.pdf.circle(circleX, circleY, layout.optionCircleSize / 2, "S");

  setFont(ctx.pdf, "bold", 8);
  setText(ctx.pdf, theme.muted);
  ctx.pdf.text(option.letter, circleX, circleY + 1, { align: "center" });

  writeWrapped(ctx.pdf, option.text, x + 12, y + 7, width - 14, {
    fontSize: 9,
    lineHeight: 4.5,
  });

  return rowHeight;
}

function renderExamMcqQuestion(ctx, slide) {
  const cardHeight = estimateMcqQuestionHeight(ctx, slide);
  const textWidth = ctx.contentWidth - layout.questionNumberSize - 8;
  setFont(ctx.pdf, "normal", 10);
  const promptLines = ctx.pdf.splitTextToSize(coerceText(slide.text), textWidth);
  const optionRows = Math.ceil((slide.options?.length ?? 0) / 2);
  ensureSpace(ctx, cardHeight + layout.questionGap);

  const cardTop = ctx.y;
  setDraw(ctx.pdf, theme.cardBorder);
  ctx.pdf.setLineWidth(0.2);
  setFill(ctx.pdf, theme.white);
  drawRoundedRect(ctx.pdf, ctx.marginX, cardTop, ctx.contentWidth, cardHeight - layout.cardPadding, "FD");

  const numberX = ctx.marginX + 4;
  const contentX = ctx.marginX + layout.questionNumberSize + 6;
  let textY = cardTop + 8;
  drawQuestionNumberBadge(ctx, slide.number, numberX, textY);

  setFont(ctx.pdf, "normal", 10);
  setText(ctx.pdf, theme.text);
  for (const line of promptLines) {
    ctx.pdf.text(line, contentX, textY);
    textY += layout.lineHeight + 0.5;
  }

  textY += 2;
  const colGap = 4;
  const colWidth = (ctx.contentWidth - 8 - colGap) / 2;
  const options = slide.options ?? [];
  for (let index = 0; index < options.length; index += 1) {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = ctx.marginX + 4 + col * (colWidth + colGap);
    const y = textY + row * 14;
    renderMcqOption(ctx, options[index], x, y, colWidth);
  }

  textY += optionRows * 14 + 2;
  if (slide.correctAnswer) {
    setFont(ctx.pdf, "bold", 8);
    setText(ctx.pdf, theme.primary);
    ctx.pdf.text(`Answer: ${slide.correctAnswer}`, contentX, textY);
    textY += 4;
    if (slide.markingGuidance) {
      setFont(ctx.pdf, "normal", 8);
      setText(ctx.pdf, theme.muted);
      ctx.pdf.text(slide.markingGuidance, contentX, textY);
    }
  }

  ctx.y = cardTop + cardHeight - layout.cardPadding + layout.questionGap;
}

function renderAnswerLines(ctx, lineCount, x, y, width, isLongAnswer) {
  let cursorY = y;
  const gap = isLongAnswer ? 7 : 5;
  for (let index = 0; index < lineCount; index += 1) {
    cursorY += gap;
    setDraw(ctx.pdf, theme.answerLine);
    ctx.pdf.setLineWidth(0.25);
    ctx.pdf.line(x, cursorY, x + width, cursorY);
  }
  return cursorY + 3;
}

function renderExamStudentMcqAnswer(ctx, slide) {
  const rowHeight = layout.studentMcqAnswerRowHeight;
  const cardHeight = rowHeight;

  const cardTop = ctx.y;
  setDraw(ctx.pdf, theme.cardBorder);
  ctx.pdf.setLineWidth(0.2);
  setFill(ctx.pdf, theme.white);
  drawRoundedRect(ctx.pdf, ctx.marginX, cardTop, ctx.contentWidth, cardHeight, "FD");

  const rowCenterY = cardTop + rowHeight / 2 + 1;
  const numberX = ctx.marginX + 4;
  drawQuestionNumberBadge(ctx, slide.number, numberX, rowCenterY + 3);

  const bubbleStartX = ctx.marginX + layout.answerNumberColumnWidth + 4;
  const bubbleLetters =
    slide.bubbleLabels ??
    ["A", "B", "C", "D"].slice(0, slide.bubbleCount ?? 4);
  let bubbleX = bubbleStartX;
  for (const letter of bubbleLetters) {
    setDraw(ctx.pdf, "#CBD5E1");
    ctx.pdf.setLineWidth(0.4);
    ctx.pdf.circle(bubbleX, rowCenterY, layout.optionCircleSize / 2, "S");
    setFont(ctx.pdf, "bold", 8);
    setText(ctx.pdf, theme.muted);
    ctx.pdf.text(letter, bubbleX, rowCenterY + 1, { align: "center" });
    bubbleX += layout.answerBubbleSpacing;
  }

  ctx.y = cardTop + cardHeight + layout.questionGap;
}

function renderExamWrittenQuestion(ctx, slide) {
  const textWidth = ctx.contentWidth - layout.questionNumberSize - 30;
  setFont(ctx.pdf, "normal", slide.isLongAnswer ? 10.5 : 10);
  const promptLines = ctx.pdf.splitTextToSize(coerceText(slide.text), textWidth);
  const hasAnswerLines = slide.lineCount > 0;
  const lineGap = slide.isLongAnswer ? 7 : 5;
  const lineBlockHeight = hasAnswerLines ? slide.lineCount * lineGap + 10 : 0;
  const promptHeight = promptLines.length * (layout.lineHeight + (slide.isLongAnswer ? 1 : 0.5));
  const answerHeight = slide.correctAnswer ? 14 : 0;
  const cardHeight =
    12 + promptHeight + lineBlockHeight + answerHeight + layout.cardPadding;

  const cardTop = ctx.y;
  setDraw(ctx.pdf, theme.cardBorder);
  ctx.pdf.setLineWidth(0.2);
  setFill(ctx.pdf, theme.white);
  drawRoundedRect(ctx.pdf, ctx.marginX, cardTop, ctx.contentWidth, cardHeight, "FD");

  const numberX = ctx.marginX + 4;
  const contentX = ctx.marginX + layout.questionNumberSize + 6;
  let textY = cardTop + 8;
  drawQuestionNumberBadge(ctx, slide.number, numberX, textY);

  setFont(ctx.pdf, "normal", slide.isLongAnswer ? 10.5 : 10);
  setText(ctx.pdf, theme.text);
  for (const line of promptLines) {
    ctx.pdf.text(line, contentX, textY);
    textY += layout.lineHeight + (slide.isLongAnswer ? 1 : 0.5);
  }

  drawMarksBadge(
    ctx,
    slide.marksLabel,
    ctx.marginX + ctx.contentWidth - 26,
    cardTop + 8,
  );

  if (hasAnswerLines) {
    const linesTop = textY + 5;
    setFill(ctx.pdf, "#FAFAFA");
    drawRoundedRect(
      ctx.pdf,
      contentX,
      linesTop,
      ctx.contentWidth - layout.questionNumberSize - 10,
      lineBlockHeight,
      "F",
    );
    textY = renderAnswerLines(
      ctx,
      slide.lineCount,
      contentX + 2,
      linesTop + 2,
      ctx.contentWidth - layout.questionNumberSize - 14,
      slide.isLongAnswer,
    );
  }

  if (slide.correctAnswer) {
    setDraw(ctx.pdf, "#F1F5F9");
    ctx.pdf.setLineWidth(0.2);
    ctx.pdf.line(contentX, textY + 1, ctx.marginX + ctx.contentWidth - 4, textY + 1);
    setFont(ctx.pdf, "bold", 8);
    setText(ctx.pdf, theme.primary);
    ctx.pdf.text(`Answer: ${slide.correctAnswer}`, contentX, textY + 6);
    if (slide.markingGuidance) {
      setFont(ctx.pdf, "normal", 8);
      setText(ctx.pdf, theme.muted);
      ctx.pdf.text(slide.markingGuidance, contentX, textY + 10);
    }
  }

  ctx.y = cardTop + cardHeight + layout.questionGap;
}

function renderExamSyllabusSummary(ctx, slide) {
  const items = slide.items ?? [];
  if (!items.length) {
    return;
  }

  const rows = Math.ceil(items.length / 3);
  const estimatedHeight = 16 + rows * 22;
  ensureSpace(ctx, estimatedHeight);

  setFill(ctx.pdf, theme.sectionBg);
  setDraw(ctx.pdf, theme.border);
  ctx.pdf.setLineWidth(0.3);
  drawRoundedRect(ctx.pdf, ctx.marginX, ctx.y, ctx.contentWidth, estimatedHeight, "FD");

  setFont(ctx.pdf, "bold", 10);
  setText(ctx.pdf, theme.primary);
  ctx.pdf.text("SYLLABUS COMPOSITION SUMMARY", ctx.marginX + 5, ctx.y + 8);

  const colWidth = (ctx.contentWidth - 16) / 3;
  const gap = 4;
  let itemY = ctx.y + 14;

  items.forEach((item, index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = ctx.marginX + 5 + col * (colWidth + gap);
    const y = itemY + row * 22;

    setDraw(ctx.pdf, theme.primary);
    ctx.pdf.setLineWidth(0.5);
    setFill(ctx.pdf, theme.white);
    drawRoundedRect(ctx.pdf, x, y, colWidth, 18, "FD");

    writeWrapped(ctx.pdf, item.topic, x + 3, y + 6, colWidth - 6, {
      fontStyle: "bold",
      fontSize: 8.5,
      lineHeight: 4,
    });
    setFont(ctx.pdf, "bold", 7);
    setText(ctx.pdf, theme.primary);
    ctx.pdf.text(item.marksLabel.toUpperCase(), x + colWidth / 2, y + 15, { align: "center" });
  });

  ctx.y += estimatedHeight + layout.sectionGap;
}

function renderOmrBubbleRow(ctx, number, y) {
  const label = `${String(number).padStart(2, "0")}.`;
  setFont(ctx.pdf, "bold", 9);
  setText(ctx.pdf, theme.text);
  ctx.pdf.text(label, ctx.marginX + 4, y);

  let bubbleX = ctx.marginX + 16;
  for (const letter of ["A", "B", "C", "D"]) {
    setDraw(ctx.pdf, theme.text);
    ctx.pdf.setLineWidth(0.35);
    ctx.pdf.circle(bubbleX, y - 1.5, 3.5, "S");
    setFont(ctx.pdf, "bold", 7);
    ctx.pdf.text(letter, bubbleX, y, { align: "center" });
    bubbleX += 10;
  }
}

function renderExamOmrSheet(ctx, slide) {
  ensureSpace(ctx, 40);
  setFont(ctx.pdf, "bold", 20);
  setText(ctx.pdf, theme.text);
  ctx.pdf.text("OMR RESPONSE SHEET", ctx.marginX, ctx.y + 6);

  setFont(ctx.pdf, "bold", 9);
  setText(ctx.pdf, theme.primary);
  ctx.pdf.text(slide.title.toUpperCase(), ctx.marginX, ctx.y + 14);

  ctx.y += 22;

  const fields = [
    ["Student Name", "______________________________"],
    ["Roll Number", "______________________________"],
    ["Date", "______________________________"],
    ["Subject", slide.subjectLine],
  ];

  fields.forEach(([label, value], index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = ctx.marginX + col * (ctx.contentWidth / 2);
    const y = ctx.y + row * 14;
    setFont(ctx.pdf, "bold", 7);
    setText(ctx.pdf, theme.text);
    ctx.pdf.text(label.toUpperCase(), x, y);
    setDraw(ctx.pdf, theme.text);
    ctx.pdf.setLineWidth(0.3);
    ctx.pdf.line(x, y + 6, x + ctx.contentWidth / 2 - 6, y + 6);
    setFont(ctx.pdf, "normal", 9);
    setText(ctx.pdf, theme.muted);
    if (label === "Subject") {
      ctx.pdf.text(value, x, y + 5.5);
    }
  });

  ctx.y += 32;
  setFill(ctx.pdf, theme.primary);
  ctx.pdf.rect(ctx.marginX, ctx.y, ctx.contentWidth, 1.2, "F");
  ctx.y += 8;

  const questions = slide.questions ?? [];
  const midpoint = Math.ceil(questions.length / 2);
  const leftColumn = questions.slice(0, midpoint);
  const rightColumn = questions.slice(midpoint);
  const colWidth = ctx.contentWidth / 2;

  const maxRows = Math.max(leftColumn.length, rightColumn.length);
  for (let row = 0; row < maxRows; row += 1) {
    ensureSpace(ctx, 10);
    const y = ctx.y;
    if (leftColumn[row] !== undefined) {
      renderOmrBubbleRow(ctx, leftColumn[row], y);
    }
    if (rightColumn[row] !== undefined) {
      const savedMargin = ctx.marginX;
      ctx.marginX = savedMargin + colWidth;
      renderOmrBubbleRow(ctx, rightColumn[row], y);
      ctx.marginX = savedMargin;
    }
    ctx.y += 9;
  }

  ctx.y += 6;
  ensureSpace(ctx, 40);
  setFill(ctx.pdf, "#F3F4F6");
  drawRoundedRect(ctx.pdf, ctx.marginX, ctx.y, ctx.contentWidth, 38, "F");
  setFont(ctx.pdf, "bold", 8);
  setText(ctx.pdf, theme.primary);
  ctx.pdf.text("EXAMINATION INSTRUCTIONS", ctx.marginX + 5, ctx.y + 7);
  const omrInstructions = [
    "Use a Blue or Black ballpoint pen only.",
    "Fill the bubble completely and dark.",
    "Make no stray marks on the sheet.",
  ];
  setFont(ctx.pdf, "normal", 8);
  setText(ctx.pdf, "#475569");
  let instructionY = ctx.y + 13;
  for (const line of omrInstructions) {
    ctx.pdf.text(`- ${line}`, ctx.marginX + 7, instructionY);
    instructionY += 5;
  }
  ctx.y += 44;
}

function renderQuestionPaperSlide(ctx, slide) {
  switch (slide.kind) {
    case "exam-paper-header":
      renderExamPaperHeader(ctx, slide);
      break;
    case "exam-instructions":
      renderExamInstructions(ctx, slide);
      break;
    case "exam-section-header":
      renderExamSectionHeader(ctx, slide);
      break;
    case "exam-mcq-question":
      renderExamMcqQuestion(ctx, slide);
      break;
    case "exam-written-question":
      renderExamWrittenQuestion(ctx, slide);
      break;
    case "exam-student-mcq-answer":
      renderExamStudentMcqAnswer(ctx, slide);
      break;
    case "exam-syllabus-summary":
      renderExamSyllabusSummary(ctx, slide);
      break;
    case "exam-omr-sheet":
      renderExamOmrSheet(ctx, slide);
      break;
    default:
      break;
  }
}

function drawQuestionPaperPageChrome(pdf, document) {
  const totalPages = pdf.getNumberOfPages();
  const footerLabel = coerceText(
    document.displayTitle || document.title || examDocumentFallbackTitle(),
  );

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    const footerY = pdfLayout.pageHeight - 8;
    setDraw(pdf, theme.cardBorder);
    pdf.setLineWidth(0.2);
    pdf.line(layout.marginX, footerY - 4, pdfLayout.pageWidth - layout.marginX, footerY - 4);

    setFont(pdf, "normal", 7.5);
    setText(pdf, theme.muted);
    pdf.text(footerLabel, layout.marginX, footerY);
    pdf.text(`Page ${page} of ${totalPages}`, pdfLayout.pageWidth / 2, footerY, {
      align: "center",
    });
  }
}

export function renderQuestionPaperPdfDocument(document) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  pdf.setProperties({
    title: document.title,
    subject: document.subject,
    author: document.author ?? "Tema",
    creator: "TutorCloud iTEMA",
  });

  setFill(pdf, theme.pageBg);
  pdf.rect(0, 0, pdfLayout.pageWidth, pdfLayout.pageHeight, "F");

  const ctx = createQuestionPaperContext(pdf, document);
  const slides = document.slides ?? [];
  for (let index = 0; index < slides.length; index += 1) {
    const slide = slides[index];
    if (slide.kind === "exam-section-header") {
      ensureSectionHeaderSpace(ctx, slide, slides[index + 1]);
    } else if (
      slide.kind === "exam-mcq-question" ||
      slide.kind === "exam-written-question" ||
      slide.kind === "exam-student-mcq-answer"
    ) {
      ensureSpace(ctx, estimateSlideBlockHeight(ctx, slide));
    }
    renderQuestionPaperSlide(ctx, slide);
  }

  drawQuestionPaperPageChrome(pdf, document);
  return pdf;
}
